<script setup>
import { ref, computed, watch } from 'vue';
import { useStore } from 'dashboard/composables/store';
import { useAccount } from 'dashboard/composables/useAccount';
import { useAlert } from 'dashboard/composables';
import { useI18n } from 'vue-i18n';
import NextButton from 'dashboard/components-next/button/Button.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import ContactAPI from 'dashboard/api/contacts';
import ClientAPI from 'dashboard/api/clients';
import PatientsAPI from 'dashboard/api/patients';

const props = defineProps({
  contactId: {
    type: [Number, String],
    required: true,
  },
});

const emit = defineEmits(['close', 'created']);

const { t } = useI18n();
const store = useStore();
const { accountId } = useAccount();

const formData = ref({
  amount: '',
  note: '',
  expirationTime: '',
  paymentStatus: 0,
  requestStatus: 0,
});

const isSubmitting = ref(false);
const isLoadingClient = ref(false);
const client = ref(null);
const patients = ref([]);
const DEFAULT_EXPIRATION_TIME = 6;
const MIN_EXPIRATION_TIME = 1;
const MAX_EXPIRATION_TIME = 24;

const normalizeExpirationTime = expirationTime => {
  if (expirationTime === '' || expirationTime === null || expirationTime === undefined) {
    return DEFAULT_EXPIRATION_TIME;
  }

  const parsedExpirationTime = Number(expirationTime);
  if (!Number.isInteger(parsedExpirationTime)) return null;
  if (parsedExpirationTime < MIN_EXPIRATION_TIME || parsedExpirationTime > MAX_EXPIRATION_TIME) return null;
  return parsedExpirationTime;
};

const handleSubmit = async () => {
  if (!formData.value.amount || Number(formData.value.amount) <= 0) {
    useAlert('Please enter a valid amount');
    return;
  }

  if (!formData.value.note || formData.value.note.trim() === '') {
    useAlert('Please enter amount and description');
    return;
  }

  const expirationTime = normalizeExpirationTime(formData.value.expirationTime);
  if (expirationTime === null) {
    useAlert('Expiration Time must be a whole number between 1 and 24');
    return;
  }

  isSubmitting.value = true;
  try {
    const result = await store.dispatch('payments/create', {
      contact_id: props.contactId,
      amount: Number(formData.value.amount),
      note: formData.value.note,
      payment_status: formData.value.paymentStatus,
      request_status: formData.value.requestStatus,
      expiration_time: expirationTime,
    });

    // Copy Stripe payment URL to clipboard if available
    if (result?.stripe_payment_url) {
      try {
        await navigator.clipboard.writeText(result.stripe_payment_url);
        useAlert('Payment created successfully. Payment link copied to clipboard.');
      } catch (clipboardError) {
        // Fallback if clipboard API fails
        console.error('Failed to copy to clipboard:', clipboardError);
        useAlert('Payment created successfully, but failed to copy to clipboard');
      }
    } else {
      useAlert('Payment created successfully, but no payment link available');
    }

    emit('created');
    handleCancel();
  } catch (error) {
    const errorMessage =
      error.response?.data?.error || error.message || 'Failed to create payment';
    useAlert(errorMessage);
  } finally {
    isSubmitting.value = false;
  }
};

const handleCancel = () => {
  formData.value = {
    amount: '',
    note: '',
    expirationTime: '',
    paymentStatus: 0,
    requestStatus: 0,
  };
  emit('close');
};

let fetchClientRequestId = 0;

const fetchClientData = async () => {
  if (!props.contactId) {
    client.value = null;
    patients.value = [];
    isLoadingClient.value = false;
    return;
  }

  const requestId = ++fetchClientRequestId;
  isLoadingClient.value = true;
  client.value = null;
  patients.value = [];

  try {
    // Fetch contact to get client_id
    const contactResponse = await ContactAPI.show(props.contactId);
    if (requestId !== fetchClientRequestId) return;

    const contact = contactResponse.data.payload;

    // Get client_id from contact (now included in the JSON response)
    const clientId = contact.client_id;

    if (!clientId) {
      return;
    }

    // Fetch client data
    const clientResponse = await ClientAPI.show(clientId);
    if (requestId !== fetchClientRequestId) return;

    client.value = clientResponse.data.payload;

    // Fetch patients for this client
    const patientsResponse = await PatientsAPI.get(1, 'name', clientId);
    if (requestId !== fetchClientRequestId) return;

    patients.value = patientsResponse.data.payload || [];
  } catch (error) {
    console.error('Error fetching client data:', error);
    // Don't show error to user, just log it
  } finally {
    if (requestId === fetchClientRequestId) {
      isLoadingClient.value = false;
    }
  }
};

const patientNames = computed(() => {
  return patients.value.map(p => p.name).join(', ') || 'No patients';
});

watch(() => props.contactId, fetchClientData, { immediate: true });
</script>

<template>
  <div class="p-4 bg-n-slate-2 rounded-lg border border-n-slate-4">
    <div class="space-y-4">
      <!-- Client Information -->
      <div v-if="client || isLoadingClient" class="p-3 bg-n-slate-3 rounded-md border border-n-slate-5">
        <h4 class="text-sm font-semibold text-n-slate-12 mb-2">Client Information</h4>
        <div v-if="isLoadingClient" class="text-sm text-n-slate-11">
          Loading client data...
        </div>
        <div v-else-if="client" class="space-y-1 text-sm">
          <div>
            <span class="font-medium text-n-slate-11">Client Name:</span>
            <span class="ml-2 text-n-slate-12">{{ client.name }}</span>
          </div>
          <div>
            <span class="font-medium text-n-slate-11">Client ID:</span>
            <span class="ml-2 text-n-slate-12">{{ client.id }}</span>
          </div>
          <div>
            <span class="font-medium text-n-slate-11">Patients:</span>
            <span class="ml-2 text-n-slate-12">{{ patientNames }}</span>
          </div>
        </div>
      </div>

      <div>
        <label class="block text-sm font-medium text-n-slate-11 mb-1">
          Amount <span class="text-n-ruby-9">*</span>
        </label>
        <Input v-model="formData.amount" type="number" placeholder="Enter amount" :disabled="isSubmitting" required />
      </div>
      <div>
        <label class="block text-sm font-medium text-n-slate-11 mb-1">
          Description <span class="text-n-ruby-9">*</span>
        </label>
        <textarea v-model="formData.note" placeholder="Enter payment description (visible to client)"
          :disabled="isSubmitting" required rows="3"
          class="w-full px-3 py-2 border border-n-slate-6 rounded-md bg-n-slate-1 text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-n-brand focus:border-transparent disabled:opacity-50 disabled:cursor-not-allowed" />
      </div>
      <div>
        <label class="block text-sm font-medium text-n-slate-11 mb-1">
          Link expiration time (hours)
        </label>
        <Input v-model="formData.expirationTime" type="number" min="1" max="24" placeholder="6"
          :disabled="isSubmitting" />
      </div>
      <div class="flex gap-2 justify-end">
        <NextButton variant="ghost" :label="'Cancel'" :disabled="isSubmitting" @click="handleCancel" />
        <NextButton variant="solid" color="blue" :label="'Create Payment'" :loading="isSubmitting"
          @click="handleSubmit" />
      </div>
    </div>
  </div>
</template>
